/**
 * Expenses Page (T163, T167)
 * Expense list with filters (date range, category, payment method, vendor)
 * Includes inline edit support with audit trail
 */

import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Receipt,
  Search,
  Plus,
  TrendingDown,
  Filter,
  BarChart3,
  Edit2,
  Trash2,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import { useToast } from '../components/ui';
import { TableListSkeleton } from '../components/ui/SkeletonLoaders';
import { useExpenses, useDeleteExpense, type ExpenseFilters } from '../hooks/useExpenses';
import ExpenseCreateModal from '../components/Expenses/ExpenseCreateModal';
import ExpenseEditModal from '../components/Expenses/ExpenseEditModal';

const CATEGORIES = [
  { value: '', label: 'All Categories' },
  { value: 'OFFICE_SUPPLIES', label: 'Office Supplies' },
  { value: 'TECHNOLOGY', label: 'Technology' },
  { value: 'SOFTWARE_SUBSCRIPTIONS', label: 'Software Subscriptions' },
  { value: 'MARKETING', label: 'Marketing' },
  { value: 'PROFESSIONAL_SERVICES', label: 'Professional Services' },
  { value: 'PROFESSIONAL_DEVELOPMENT', label: 'Professional Development' },
  { value: 'RENT', label: 'Rent' },
  { value: 'UTILITIES', label: 'Utilities' },
  { value: 'INSURANCE', label: 'Insurance' },
  { value: 'TRAVEL', label: 'Travel' },
  { value: 'MEALS', label: 'Meals' },
  { value: 'OTHER', label: 'Other' },
];

const PAYMENT_METHODS = [
  { value: '', label: 'All Methods' },
  { value: 'CREDIT_CARD', label: 'Credit Card' },
  { value: 'DEBIT_CARD', label: 'Debit Card' },
  { value: 'BANK_TRANSFER', label: 'Bank Transfer' },
  { value: 'CASH', label: 'Cash' },
  { value: 'CHECK', label: 'Check' },
  { value: 'APPLE_PAY', label: 'Apple Pay' },
  { value: 'GOOGLE_PAY', label: 'Google Pay' },
  { value: 'OTHER', label: 'Other' },
];

export default function ExpensesPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { showToast } = useToast();
  const deleteExpense = useDeleteExpense();

  // Filter state from URL (T155)
  const [filters, setFilters] = useState<ExpenseFilters>({
    category: searchParams.get('category') || undefined,
    paymentMethod: searchParams.get('paymentMethod') || undefined,
    vendor: searchParams.get('vendor') || undefined,
    dateFrom: searchParams.get('dateFrom') || undefined,
    dateTo: searchParams.get('dateTo') || undefined,
    search: searchParams.get('search') || undefined,
    sort: searchParams.get('sort') || 'date',
    order: searchParams.get('order') || 'desc',
    page: parseInt(searchParams.get('page') || '1', 10),
  });

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingExpenseId, setEditingExpenseId] = useState<string | null>(null);

  const { data, isLoading } = useExpenses(filters);

  // Sync filters to URL
  const updateFilters = (newFilters: Partial<ExpenseFilters>) => {
    const updated = { ...filters, ...newFilters, page: newFilters.page ?? 1 };
    setFilters(updated);

    const params = new URLSearchParams();
    Object.entries(updated).forEach(([key, val]) => {
      if (val && val !== 'date' && val !== 'desc' && val !== 1) {
        params.set(key, String(val));
      }
    });
    setSearchParams(params, { replace: true });
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Delete this expense?')) {
      return;
    }
    try {
      await deleteExpense.mutateAsync(id);
      showToast('success', 'Expense deleted');
    } catch {
      showToast('error', 'Failed to delete expense');
    }
  };

  const expenses = data?.data || [];
  const pagination = data?.pagination;
  const summary = data?.summary;

  return (
    <div className="space-y-6 pb-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="space-y-2">
          <h1>Expenses</h1>
          <p className="text-muted-foreground">Track and manage business expenses</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => navigate('/expenses/report')}
            className="claude-button claude-button-ghost"
          >
            <BarChart3 className="w-4 h-4" />
            Report
          </button>
          <button
            onClick={() => setShowCreateModal(true)}
            className="claude-button claude-button-primary"
          >
            <Plus className="w-4 h-4" />
            Add Expense
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      {summary && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          <div className="claude-card p-6 hover:shadow-md transition-shadow">
            <div className="flex items-start justify-between">
              <div className="space-y-3 flex-1">
                <p className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
                  Total Expenses
                </p>
                <p className="text-3xl font-semibold tracking-tight">
                  {summary.grandTotalFormatted}
                </p>
              </div>
              <div className="w-12 h-12 rounded-lg bg-destructive/10 flex items-center justify-center flex-shrink-0">
                <TrendingDown className="w-6 h-6 text-destructive" />
              </div>
            </div>
          </div>

          <div className="claude-card p-6 hover:shadow-md transition-shadow">
            <div className="flex items-start justify-between">
              <div className="space-y-3 flex-1">
                <p className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
                  Total Records
                </p>
                <p className="text-3xl font-semibold tracking-tight">{pagination?.total || 0}</p>
              </div>
              <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                <Receipt className="w-6 h-6 text-primary" />
              </div>
            </div>
          </div>

          <div className="claude-card p-6 hover:shadow-md transition-shadow">
            <div className="flex items-start justify-between">
              <div className="space-y-3 flex-1">
                <p className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
                  Categories Used
                </p>
                <p className="text-3xl font-semibold tracking-tight">
                  {Object.keys(summary.byCategory || {}).length}
                </p>
              </div>
              <div className="w-12 h-12 rounded-lg bg-success/10 flex items-center justify-center flex-shrink-0">
                <Filter className="w-6 h-6 text-success" />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="claude-card p-4">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3">
          {/* Search */}
          <div className="relative lg:col-span-2">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <input
              type="text"
              placeholder="Search expenses..."
              value={filters.search || ''}
              onChange={(e) => updateFilters({ search: e.target.value || undefined })}
              className="w-full pl-10 pr-4 py-2 text-sm bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20"
            />
          </div>

          {/* Category */}
          <select
            value={filters.category || ''}
            onChange={(e) => updateFilters({ category: e.target.value || undefined })}
            className="px-3 py-2 text-sm bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20"
          >
            {CATEGORIES.map((c) => (
              <option key={c.value} value={c.value}>
                {c.label}
              </option>
            ))}
          </select>

          {/* Date From */}
          <input
            type="date"
            value={filters.dateFrom || ''}
            onChange={(e) => updateFilters({ dateFrom: e.target.value || undefined })}
            className="px-3 py-2 text-sm bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20"
            placeholder="From date"
          />

          {/* Date To */}
          <input
            type="date"
            value={filters.dateTo || ''}
            onChange={(e) => updateFilters({ dateTo: e.target.value || undefined })}
            className="px-3 py-2 text-sm bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20"
            placeholder="To date"
          />
        </div>

        {/* Second row - payment method */}
        <div className="flex gap-3 mt-3">
          <select
            value={filters.paymentMethod || ''}
            onChange={(e) => updateFilters({ paymentMethod: e.target.value || undefined })}
            className="px-3 py-2 text-sm bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20"
          >
            {PAYMENT_METHODS.map((m) => (
              <option key={m.value} value={m.value}>
                {m.label}
              </option>
            ))}
          </select>

          <select
            value={`${filters.sort || 'date'}-${filters.order || 'desc'}`}
            onChange={(e) => {
              const [sort, order] = e.target.value.split('-');
              updateFilters({ sort, order });
            }}
            className="px-3 py-2 text-sm bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20"
          >
            <option value="date-desc">Newest First</option>
            <option value="date-asc">Oldest First</option>
            <option value="amount-desc">Highest Amount</option>
            <option value="amount-asc">Lowest Amount</option>
            <option value="category-asc">Category A-Z</option>
          </select>
        </div>
      </div>

      {/* Expense List */}
      {isLoading && <TableListSkeleton columns={7} rows={6} title="expenses" />}

      {!isLoading && expenses.length === 0 && (
        <div className="text-center py-12">
          <Receipt className="w-12 h-12 text-muted-foreground/30 mx-auto mb-4" />
          <h3 className="text-lg font-medium mb-1">No expenses found</h3>
          <p className="text-muted-foreground text-sm mb-4">
            {filters.search || filters.category
              ? 'Try adjusting your filters'
              : 'Add your first expense to get started'}
          </p>
          <button
            onClick={() => setShowCreateModal(true)}
            className="claude-button claude-button-primary"
          >
            <Plus className="w-4 h-4" />
            Add Expense
          </button>
        </div>
      )}

      {!isLoading && expenses.length > 0 && (
        <div className="claude-card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border bg-muted/30">
                  <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                    Date
                  </th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                    Vendor
                  </th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                    Category
                  </th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                    Description
                  </th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                    Amount
                  </th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                    Method
                  </th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {expenses.map((expense) => (
                  <tr key={expense.id} className="hover:bg-muted/20 transition-colors">
                    <td className="px-4 py-3 text-sm whitespace-nowrap">
                      {new Date(expense.date).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-3 text-sm font-medium">{expense.vendor || '—'}</td>
                    <td className="px-4 py-3">
                      <span className="claude-badge claude-badge-default text-xs">
                        {expense.categoryLabel}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-muted-foreground max-w-xs truncate">
                      {expense.description}
                    </td>
                    <td className="px-4 py-3 text-sm font-semibold text-right whitespace-nowrap">
                      {expense.amountFormatted}
                    </td>
                    <td className="px-4 py-3 text-sm text-muted-foreground whitespace-nowrap">
                      {expense.paymentMethod
                        ? expense.paymentMethod
                            .split('_')
                            .map((w: string) => w.charAt(0) + w.slice(1).toLowerCase())
                            .join(' ')
                        : '—'}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex justify-end gap-1">
                        {expense.hasReceipt && (
                          <a
                            href={expense.receiptUrl || '#'}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="claude-button claude-button-ghost p-1.5"
                            title="View receipt"
                          >
                            <Receipt className="w-3.5 h-3.5" />
                          </a>
                        )}
                        <button
                          onClick={() => setEditingExpenseId(expense.id)}
                          className="claude-button claude-button-ghost p-1.5"
                          title="Edit"
                        >
                          <Edit2 className="w-3.5 h-3.5" />
                        </button>
                        <button
                          onClick={() => handleDelete(expense.id)}
                          className="claude-button claude-button-ghost p-1.5 text-destructive hover:text-destructive"
                          title="Delete"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Pagination */}
      {pagination && pagination.totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            Showing {(pagination.page - 1) * pagination.limit + 1}–
            {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total}
          </p>
          <div className="flex items-center gap-2">
            <button
              onClick={() => updateFilters({ page: Math.max(1, (filters.page || 1) - 1) })}
              disabled={(filters.page || 1) <= 1}
              className="claude-button claude-button-ghost p-2 disabled:opacity-30"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <span className="text-sm text-muted-foreground">
              Page {pagination.page} of {pagination.totalPages}
            </span>
            <button
              onClick={() =>
                updateFilters({
                  page: Math.min(pagination.totalPages, (filters.page || 1) + 1),
                })
              }
              disabled={(filters.page || 1) >= pagination.totalPages}
              className="claude-button claude-button-ghost p-2 disabled:opacity-30"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Create Modal */}
      {showCreateModal && (
        <ExpenseCreateModal
          onClose={() => setShowCreateModal(false)}
          onSuccess={() => {
            setShowCreateModal(false);
            showToast('success', 'Expense created');
          }}
        />
      )}

      {/* Edit Modal */}
      {editingExpenseId && (
        <ExpenseEditModal
          expenseId={editingExpenseId}
          onClose={() => setEditingExpenseId(null)}
          onSuccess={() => {
            setEditingExpenseId(null);
            showToast('success', 'Expense updated');
          }}
        />
      )}
    </div>
  );
}
